import { objectWithoutProperties as _objectWithoutProperties, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import * as React from 'react';
import classNames from 'classnames';
import styles from './TabNavigationItem.module.scss.js';
import { applyCommonProps } from '../utility/commonProps.js';
import { getTarget } from '../utility/getTarget.js';

var _excluded = ["className", "children", "isCurrent", "leadingIcon", "target", "trailing"];
/**
 * Tab Navigation Item
 *
 * {@link https://livingdesign.walmart.com/components/tab-navigation/ Guidelines}
 * {@link https://livingdesign.walmart.com/components/tab-navigation/react/ React documentation}
 *
 * ### Content
 *
 * #### Leading icon
 *
 * Use a leading icon as an additional visual indicator to distinguish a Tab Navigation Item from its neighbors. Set the leading icon using the `leadingIcon` prop:
 *
 * ```tsx
 * import * as React from "react";
 * import * as Icon from "@livingdesign/icons";
 * import { TabNavigationItem } from "@walmart-web/livingdesign-components";
 *
 * export default () => {
 *   return (
 *     <TabNavigationItem leadingIcon={<Icon.User />} href="/profile/">
 *       Profile
 *     </TabNavigationItem>
 *   );
 * };
 * ```
 *
 * ### Features
 *
 * #### Current location
 *
 * The Tab Navigation Item accepts an optional `isCurrent` prop that represents the current page.
 *
 * ```tsx
 * <TabNavigationItem href="/1/" isCurrent>One</TabNavigationItem>
 * ```
 *
 * ### Interaction
 *
 * #### Linking
 *
 * Tab Navigation Item returns an anchor element. It requires a `href` property, which is forwarded to the anchor.
 *
 * Set the optional `target` property to change where the anchor opens.
 *
 * ```tsx
 * <TabNavigationItem href="https://www.walmart.com" target="_blank">
 *   Walmart
 * </TabNavigationItem>
 * ```
 */
var TabNavigationItem = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var _applyCommonProps = applyCommonProps(props, TabNavigationItem),
    className = _applyCommonProps.className,
    children = _applyCommonProps.children,
    _applyCommonProps$isC = _applyCommonProps.isCurrent,
    isCurrent = _applyCommonProps$isC === void 0 ? false : _applyCommonProps$isC,
    initialLeadingIcon = _applyCommonProps.leadingIcon,
    target = _applyCommonProps.target,
    trailing = _applyCommonProps.trailing,
    rest = _objectWithoutProperties(_applyCommonProps, _excluded);
  var leadingIcon = /*#__PURE__*/React.isValidElement(initialLeadingIcon) ? /*#__PURE__*/React.cloneElement(initialLeadingIcon, {
    size: 'small'
  }) : initialLeadingIcon;
  return /*#__PURE__*/React.createElement("a", _extends({
    className: classNames(styles.tabNavigationItem, isCurrent && styles.isCurrent, className),
    ref: ref
  }, isCurrent && {
    'aria-current': 'page'
  }, getTarget(target), rest), leadingIcon && /*#__PURE__*/React.createElement("span", {
    className: styles.leadingIcon
  }, leadingIcon), /*#__PURE__*/React.createElement("span", {
    className: styles.label
  }, children), trailing && /*#__PURE__*/React.createElement("span", {
    className: styles.trailing
  }, trailing));
});
TabNavigationItem.displayName = 'TabNavigationItem';

export { TabNavigationItem };
